import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { resolveImage, formatINR, formatApiErrorDetail } from "@/lib/api";
import { loadRazorpayScript } from "@/lib/razorpay";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Truck, Lock, CreditCard } from "lucide-react";

export default function Checkout() {
  const { cart, fetchCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [placed, setPlaced] = useState(false);
  const [error, setError] = useState("");
  const [payMethod, setPayMethod] = useState("Razorpay");
  const [form, setForm] = useState({
    name: "", mobile: "", email: "",
    address: "", city: "", state: "", pincode: "",
  });

  useEffect(() => {
    if (user && user !== false) {
      setForm((f) => ({
        ...f,
        name: user.name || "",
        email: user.email || "",
        mobile: user.mobile || "",
        ...(user.addresses?.[0] || {}),
      }));
    }
  }, [user]);

  useEffect(() => {
    if (!placed && cart.items.length === 0) navigate("/cart");
  }, [cart.items.length, navigate, placed]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{6}$/.test(form.pincode)) { setError("Enter a valid 6-digit PIN code"); return; }
    if (!/^\d{10}$/.test(form.mobile)) { setError("Enter a valid 10-digit mobile number"); return; }

    if (payMethod === "Razorpay") {
      return payWithRazorpay();
    }

    setLoading(true);
    try {
      const { data } = await api.post("/orders", { ...form, payment_method: "COD" });
      setPlaced(true);
      toast.success("Order placed successfully!");
      navigate(`/order-confirmation/${data.id}`, { replace: true });
      fetchCart();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const payWithRazorpay = async () => {
    setLoading(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        setError("Could not load payment gateway. Please try again.");
        return;
      }
      const { data: rp } = await api.post("/payments/razorpay/create-cart-order");
      const rzp = new window.Razorpay({
        key: rp.key_id,
        amount: rp.amount,
        currency: rp.currency,
        order_id: rp.order_id,
        name: "ChainSmart",
        description: `Order payment · ${cart.items.length} item(s)`,
        image: "/logo192.png",
        theme: { color: "#C9A227" },
        prefill: { name: form.name, email: form.email, contact: form.mobile },
        handler: async (response) => {
          try {
            const { data: result } = await api.post("/payments/razorpay/verify-cart", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              ...form,
            });
            setPlaced(true);
            toast.success("Payment successful! Your order is confirmed.");
            navigate(`/order-confirmation/${result.order_id}`, { replace: true });
            fetchCart();
          } catch {
            toast.error("Payment could not be verified. If money was deducted, please contact support.");
          }
        },
        modal: { ondismiss: () => { toast.info("Payment cancelled"); setLoading(false); } },
      });
      rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); });
      rzp.open();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="checkout-page">
      <h1 className="font-serif text-4xl lg:text-5xl text-obsidian mb-8">Checkout</h1>

      {error && <div className="bg-discount/10 border border-discount/30 text-discount text-sm px-4 py-3 mb-6 rounded-sm" data-testid="checkout-error">{error}</div>}

      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_380px] gap-10">
        <div className="space-y-8">
          {/* Customer */}
          <section className="bg-white border border-black/10 p-6">
            <h2 className="font-serif text-2xl text-obsidian mb-5">Customer Details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name" value={form.name} onChange={set("name")} testid="co-name" />
              <Field label="Mobile" value={form.mobile} onChange={set("mobile")} maxLength={10} testid="co-mobile" />
              <div className="sm:col-span-2">
                <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="co-email" />
              </div>
            </div>
          </section>

          {/* Address */}
          <section className="bg-white border border-black/10 p-6">
            <h2 className="font-serif text-2xl text-obsidian mb-5">Delivery Address</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Field label="Address" value={form.address} onChange={set("address")} testid="co-address" />
              </div>
              <Field label="City" value={form.city} onChange={set("city")} testid="co-city" />
              <Field label="State" value={form.state} onChange={set("state")} testid="co-state" />
              <Field label="PIN Code" value={form.pincode} onChange={set("pincode")} maxLength={6} testid="co-pincode" />
            </div>
          </section>

          {/* Payment */}
          <section className="bg-white border border-black/10 p-6">
            <h2 className="font-serif text-2xl text-obsidian mb-5">Payment Method</h2>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setPayMethod("Razorpay")}
                className={`w-full flex items-center gap-3 border p-4 rounded-sm text-left transition-colors ${payMethod === "Razorpay" ? "border-gold bg-gold/5" : "border-black/10 hover:border-gold/50"}`}
                data-testid="pay-razorpay"
              >
                <span className={`w-4 h-4 rounded-full border-2 shrink-0 ${payMethod === "Razorpay" ? "border-gold-dark bg-gold-dark" : "border-black/30"}`} />
                <CreditCard className="w-5 h-5 text-gold-dark" />
                <div>
                  <p className="font-semibold text-obsidian">Pay Online (Razorpay)</p>
                  <p className="text-xs text-muted-foreground">Cards, UPI, Netbanking &amp; Wallets — secure checkout.</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setPayMethod("COD")}
                className={`w-full flex items-center gap-3 border p-4 rounded-sm text-left transition-colors ${payMethod === "COD" ? "border-gold bg-gold/5" : "border-black/10 hover:border-gold/50"}`}
                data-testid="pay-cod"
              >
                <span className={`w-4 h-4 rounded-full border-2 shrink-0 ${payMethod === "COD" ? "border-gold-dark bg-gold-dark" : "border-black/30"}`} />
                <Truck className="w-5 h-5 text-gold-dark" />
                <div>
                  <p className="font-semibold text-obsidian">Cash on Delivery</p>
                  <p className="text-xs text-muted-foreground">Pay when your order is delivered.</p>
                </div>
              </button>
            </div>
          </section>
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-24 h-fit bg-white border border-black/10 p-6" data-testid="checkout-summary">
          <h2 className="font-serif text-2xl text-obsidian mb-5">Order Summary</h2>
          <div className="space-y-3 max-h-64 overflow-y-auto mb-4 pr-1">
            {cart.items.map((it) => (
              <div key={it.product_id} className="flex gap-3">
                <img src={resolveImage(it.image)} alt="" className="w-12 h-14 object-cover bg-secondary shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-obsidian truncate">{it.name}</p>
                  <p className="text-xs text-muted-foreground">Qty {it.quantity} · {formatINR(it.price)}</p>
                </div>
                <span className="text-sm font-semibold">{formatINR(it.line_total)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2.5 text-sm border-t border-black/10 pt-4">
            <div className="flex justify-between"><span className="text-muted-foreground">Total MRP</span><span>{formatINR(cart.subtotal_mrp)}</span></div>
            <div className="flex justify-between text-green-700"><span>Total Savings</span><span>- {formatINR(cart.total_savings)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span className="text-green-700">Free</span></div>
            <div className="border-t border-black/10 pt-3 flex justify-between items-baseline">
              <span className="font-semibold">Total</span>
              <span className="font-bold text-2xl" data-testid="checkout-total">{formatINR(cart.total)}</span>
            </div>
          </div>
          <button type="submit" disabled={loading} className="w-full mt-5 inline-flex items-center justify-center gap-2 bg-gold text-obsidian font-semibold py-3.5 hover:bg-gold-light transition-colors disabled:opacity-50 rounded-sm" data-testid="place-order-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} {payMethod === "Razorpay" ? `Pay ${formatINR(cart.total)}` : "Place Order (COD)"}
          </button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, testid, type = "text", ...props }) => (
  <div>
    <Label>{label}</Label>
    <Input type={type} required className="mt-1.5 rounded-sm" data-testid={testid} {...props} />
  </div>
);
